import { Survey } from 'Containers/ProfilePage/types';
import { ProviderSelectionContextType } from 'Contexts/providerSelectionContext';
import { DoctorLocation, DoctorType, Insurance, Recommendation } from 'Types/entities';

import { currentPlanYearCodes } from '../constants';

export const getFilteredProviders = (
  providerDetails: DoctorType[],
  availablePlans: Recommendation[],
  shouldShowPreferredProviderTiers: boolean = false,
  survey: Survey,
): ProviderSelectionContextType['filteredProviders'] => {
  const filteredProviders: ProviderSelectionContextType['filteredProviders'] = {};
  availablePlans.forEach((recommendation) => {
    const inNetworkProviderList: DoctorType[] = [];
    const outOfNetworkProviderList: DoctorType[] = [];
    const preferredNetworkProviderList: DoctorType[] = [];
    // Add out of network or in network provider
    providerDetails.forEach((provider: DoctorType) => {
      if (shouldShowPreferredProviderTiers) {
        const mostPreferredInsurances = provider.selected_location?.most_preferred_insurances;
        const hasPreferredFromSelectedLocation =
          mostPreferredInsurances &&
          mostPreferredInsurances.length > 0 &&
          mostPreferredInsurances.some((uuid) => recommendation.plan.docfinder_network_uuids?.includes(uuid));

        if (hasPreferredFromSelectedLocation) {
          preferredNetworkProviderList.push(provider);
        } else {
          const hasPreferredFromAllLocations = provider.locations?.some((location: DoctorLocation) => {
            return location.insurances?.some((insurance: Insurance) => {
              return (
                recommendation.plan.docfinder_network_uuids?.includes(insurance.uuid) && insurance.preferred
              );
            });
          });

          if (hasPreferredFromAllLocations) {
            preferredNetworkProviderList.push(provider);
          }
        }
      }

      const { insurancesUuids, showAsPerPlanYear } = getInsuranceUuids(provider, survey);
      if (showAsPerPlanYear) {
        if (
          insurancesUuids &&
          insurancesUuids.length > 0 &&
          insurancesUuids.some((uuid) => recommendation.plan.docfinder_network_uuids?.includes(uuid))
        ) {
          inNetworkProviderList.push(provider);
        } else {
          outOfNetworkProviderList.push(provider);
        }
      } else if (
        provider.insurances?.some((insurance: Insurance) =>
          recommendation.plan.docfinder_network_uuids?.includes(insurance.uuid),
        )
      ) {
        inNetworkProviderList.push(provider);
      } else {
        outOfNetworkProviderList.push(provider);
      }
    });

    filteredProviders[recommendation.plan.external_id] = shouldShowPreferredProviderTiers
      ? {
          inNetwork: inNetworkProviderList,
          outOfNetwork: outOfNetworkProviderList,
          preferredNetwork: preferredNetworkProviderList,
        }
      : {
          inNetwork: inNetworkProviderList,
          outOfNetwork: outOfNetworkProviderList,
        };
  });

  return filteredProviders;
};

const getInsuranceUuids = (
  provider: DoctorType,
  survey: Survey,
): { insurancesUuids: string[] | null; showAsPerPlanYear: boolean } => {
  // Determine which field to use based on enrollment_question
  const isCurrentPlan = currentPlanYearCodes.includes(survey.enrollment_question);
  const selectedField = isCurrentPlan ? 'current_plan_year_insurances' : 'next_plan_year_insurances';
  // Get the insurance UUIDs from the appropriate field
  const insurancesUuids = provider.selected_location?.[selectedField];
  const showAsPerPlanYear = Array.isArray(insurancesUuids) && insurancesUuids.length > 0;

  return {
    insurancesUuids: showAsPerPlanYear ? insurancesUuids : null,
    showAsPerPlanYear,
  };
};
